import { ActionInvocationOrigin, withActionInvocationOrigin } from './actionInvocationOrigin';

/** Message controllers stay mounted but hidden; the dock invokes their live DOM buttons. */
export interface MirroredAction {
  button: HTMLButtonElement;
  label: string;
  title: string;
  disabled: boolean;
  busy: boolean;
  selected: boolean;
  attentionPrimary: boolean;
  placement?: 'pinned' | 'secondary';
  iconClass: string | null;
  stateClasses: string;
}
export interface ActionMirrorSource {
  element: HTMLElement;
  ownerId: string;
  ownerLabel: string;
  current: boolean;
  inlineAtTail: boolean;
  attentionActive: boolean;
  retryable: boolean;
  actions: MirroredAction[];
  errors: string[];
}
interface Region {
  scope: string;
  priority: number;
  source: ActionMirrorSource | null;
  bars: HTMLElement[];
  readSource: () => ActionMirrorSource | null;
}
const regions = new Map<symbol, Region>();
const snapshots = new Map<string, ActionMirrorSource | null>();
const listeners = new Map<string, Set<() => void>>();
const invoking = new WeakSet<HTMLButtonElement>();
const originalSelection = new Map<HTMLElement, string | null>();

export const actionMirrorScope = (projectId?: string | null, sessionId?: string | null): string => (
  JSON.stringify([projectId ?? null, sessionId ?? null])
);
export const mirrorSnapshot = (scope: string): ActionMirrorSource | null => snapshots.get(scope) ?? null;
export const subscribeActionMirror = (scope: string, listener: () => void): (() => void) => {
  const group = listeners.get(scope) ?? new Set();

  group.add(listener);
  listeners.set(scope, group);

  return () => {
    group.delete(listener);
    if (!group.size) listeners.delete(scope);
  };
};
const sameSource = (a: ActionMirrorSource | null, b: ActionMirrorSource | null): boolean => a === b || Boolean(
  a && b && a.element === b.element && a.ownerId === b.ownerId && a.ownerLabel === b.ownerLabel
  && a.current === b.current && a.inlineAtTail === b.inlineAtTail && a.attentionActive === b.attentionActive
  && a.retryable === b.retryable
  && a.errors.length === b.errors.length && a.errors.every((error, index) => error === b.errors[index])
  && a.actions.length === b.actions.length && a.actions.every((action, index) => {
    const next = b.actions[index];

    return action.button === next.button && action.label === next.label && action.title === next.title
      && action.disabled === next.disabled && action.busy === next.busy && action.selected === next.selected
      && action.attentionPrimary === next.attentionPrimary
      && action.placement === next.placement
      && action.iconClass === next.iconClass && action.stateClasses === next.stateClasses;
  }),
);
const latestRegion = (current: Region | null, next: Region): Region => {
  if (!current) return next;
  if (current.source!.retryable !== next.source!.retryable) return next.source!.retryable ? next : current;
  const order = current.source!.element.compareDocumentPosition(next.source!.element);

  if (!(order & Node.DOCUMENT_POSITION_DISCONNECTED) && current.source!.element !== next.source!.element) {
    return order & Node.DOCUMENT_POSITION_FOLLOWING ? next : current;
  }

  return next.priority > current.priority ? next : current;
};
const restoreInlineSelection = (bar: HTMLElement, original: string | null): void => {
  if (original === null) bar.removeAttribute('data-action-source-selected');
  else bar.setAttribute('data-action-source-selected', original);
  originalSelection.delete(bar);
};
const updateInlineSelection = (): void => {
  const selected = new Map<HTMLElement, boolean>();

  regions.forEach((region) => region.bars.forEach((bar) => selected.set(bar,
    Boolean(selected.get(bar) || mirrorSnapshot(region.scope)?.element === bar))));
  originalSelection.forEach((original, bar) => {
    if (!selected.has(bar)) restoreInlineSelection(bar, original);
  });
  selected.forEach((visible, bar) => {
    if (!originalSelection.has(bar)) originalSelection.set(bar, bar.getAttribute('data-action-source-selected'));
    const value = String(visible);

    if (bar.dataset.actionSourceSelected !== value) bar.dataset.actionSourceSelected = value;
  });
};
const publish = (scope: string): void => {
  const candidates = Array.from(regions.values()).filter((region) => (
    region.scope === scope && region.source?.element.isConnected
  ));
  const latest = candidates.reduce<Region | null>(latestRegion, null)?.source ?? null;
  const changed = !sameSource(mirrorSnapshot(scope), latest);

  if (changed && latest) snapshots.set(scope, latest);
  else if (changed) snapshots.delete(scope);
  updateInlineSelection();
  if (changed) listeners.get(scope)?.forEach((listener) => listener());
};

export const updateMirrorRegion = (token: symbol, region: Region): void => {
  const previous = regions.get(token);

  regions.set(token, region);
  if (previous && previous.scope !== region.scope) publish(previous.scope);
  publish(region.scope);
};
export const removeMirrorRegion = (token: symbol): void => {
  const previous = regions.get(token);

  regions.delete(token);
  if (previous) publish(previous.scope);
};
export const buttonIsDisabled = (button: HTMLButtonElement): boolean => button.disabled
  || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true'
  || button.matches('.ai-overview-action-btn--done, .agent-suggestion-pill--triggered, .agent-suggestion-pill--locked');
const refreshMirrorScope = (scope: string): ActionMirrorSource | null => {
  regions.forEach((region) => {
    if (region.scope === scope) region.source = region.readSource();
  });
  publish(scope);

  return mirrorSnapshot(scope);
};

export const invokeMirroredAction = (
  scope: string, source: ActionMirrorSource, action: MirroredAction, origin: ActionInvocationOrigin,
): void => {
  const current = refreshMirrorScope(scope);
  const button = action.button;

  if (!current || current.element !== source.element || !current.actions.some((item) => item.button === button)
    || !button.isConnected || buttonIsDisabled(button) || invoking.has(button)) return;
  invoking.add(button);
  try {
    withActionInvocationOrigin(origin, () => button.click());
  } finally {
    // Share the source controller; only coalesce duplicate clicks in this event turn.
    void Promise.resolve().then(() => invoking.delete(button));
  }
};
